import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import StarRating from "./StarRating";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { findProfaneWords } from "@/lib/profanityFilter";
import { checkReviewQuality, checkFieldQuality } from "@/lib/gibberishFilter";
import { getReviewTypeConfig, type Review } from "@/lib/mockData";

interface EditReviewDialogProps {
  review: Review;
  open: boolean;
  onClose: () => void;
  onSaved: (patch: Partial<Review>) => void;
}

const EditReviewDialog = ({ review, open, onClose, onSaved }: EditReviewDialogProps) => {
  const [major, setMajor] = useState(review.major ?? "");
  const [title, setTitle] = useState(review.title ?? "");
  const [organization, setOrganization] = useState(review.organization ?? "");
  const [location, setLocation] = useState(review.location ?? "");
  const [rating, setRating] = useState(review.rating);
  const [difficulty, setDifficulty] = useState(review.difficulty);
  const [wouldDoAgain, setWouldDoAgain] = useState<boolean>(!!review.wouldDoAgain);
  const [text, setText] = useState(review.review ?? "");
  const [startDate, setStartDate] = useState(review.startDate ?? "");
  const [endDate, setEndDate] = useState(review.endDate ?? "");
  const [compensation, setCompensation] = useState(review.compensation ?? "");
  const [interviewInfo, setInterviewInfo] = useState(review.interviewInfo ?? "");
  const [responsibilities, setResponsibilities] = useState(review.responsibilities ?? "");
  const showCompensation = getReviewTypeConfig(review.type).showCompensation;
  const [saving, setSaving] = useState(false);


  const handleSave = async () => {
    if (!organization.trim()) {
      toast({ title: "Organization is required", variant: "destructive" });
      return;
    }
    for (const [val, label] of [
      [major, "Rotation / internship type"],
      [organization, review.type === "rotation" ? "Hospital / clinic" : "Company"],
      [title, "Title"],
      [location, "Location"],
    ] as Array<[string, string]>) {
      const fq = checkFieldQuality(val, label);
      if (!fq.ok) {
        toast({ title: "Please check your entry", description: fq.reason, variant: "destructive" });
        return;
      }
    }
    if (text.trim()) {
      const quality = checkReviewQuality(text);
      if (!quality.ok) {
        toast({ title: "Please improve your review", description: quality.reason, variant: "destructive" });
        return;
      }
      const offending = Array.from(new Set([...findProfaneWords(text), ...findProfaneWords(organization)]));
      if (offending.length > 0) {
        toast({
          title: "Please adjust your language",
          description: `Your review contains vulgar or offensive language (${offending.slice(0, 3).join(", ")}).`,
          variant: "destructive",
        });
        return;
      }
    }

    setSaving(true);
    const patch = {
      major: major.trim(),
      title: title.trim() || major.trim(),
      organization: organization.trim(),
      location: location.trim(),
      rating,
      difficulty,
      would_do_again: wouldDoAgain,
      review: text,
      start_date: startDate || null,
      end_date: endDate || null,
      compensation: showCompensation ? compensation.trim() || null : null,
      interview_info: interviewInfo.trim() || null,
      responsibilities: responsibilities.trim() || null,
    };
    const { error } = await supabase.from("reviews").update(patch as any).eq("id", review.id);
    setSaving(false);

    if (error) {
      toast({ title: "Could not save changes", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Review updated", description: "Your changes are now live." });
    onSaved({
      major: patch.major,
      title: patch.title,
      organization: patch.organization,
      location: patch.location,
      rating,
      difficulty,
      wouldDoAgain,
      review: text,
      startDate: patch.start_date,
      endDate: patch.end_date,
      compensation: patch.compensation,
      interviewInfo: patch.interview_info,
      responsibilities: patch.responsibilities,
    });

    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif">Edit your review</DialogTitle>
          <DialogDescription>
            Your review stays public and anonymous — only you can edit it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{review.type === "rotation" ? "Rotation type" : "Internship field"}</Label>
            <Input value={major} onChange={(e) => setMajor(e.target.value)} />
          </div>
          {review.type === "internship" && (
            <div className="space-y-1.5">
              <Label>Position title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{review.type === "rotation" ? "Hospital / clinic" : "Company"}</Label>
            <Input value={organization} onChange={(e) => setOrganization(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Location</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Start date</Label>
              <Input type="date" value={startDate ?? ""} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>End date</Label>
              <Input type="date" value={endDate ?? ""} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Overall rating</Label>
            <StarRating rating={rating} interactive onRatingChange={setRating} size="lg" />
          </div>
          <div className="space-y-1.5">
            <Label>Difficulty</Label>
            <StarRating rating={difficulty} interactive onRatingChange={setDifficulty} size="lg" />
          </div>
          <div className="space-y-1.5">
            <Label>Would you do it again?</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={wouldDoAgain ? "default" : "outline"}
                size="sm"
                onClick={() => setWouldDoAgain(true)}
              >
                👍 Yes
              </Button>
              <Button
                type="button"
                variant={!wouldDoAgain ? "default" : "outline"}
                size="sm"
                onClick={() => setWouldDoAgain(false)}
              >
                👎 No
              </Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Your review</Label>
            <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} />
          </div>
          <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3">
            <Label className="text-sm">Help get this experience to 100%</Label>
            {showCompensation && (
              <div className="space-y-1.5">
                <Label className="text-xs font-normal text-muted-foreground">Compensation / stipend</Label>
                <Input value={compensation} onChange={(e) => setCompensation(e.target.value)} placeholder="e.g. Unpaid, $22/hr" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-normal text-muted-foreground">Interview / application process</Label>
              <Textarea rows={2} value={interviewInfo} onChange={(e) => setInterviewInfo(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-normal text-muted-foreground">What did you actually do?</Label>
              <Textarea rows={2} value={responsibilities} onChange={(e) => setResponsibilities(e.target.value)} />
            </div>
          </div>

        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditReviewDialog;
